import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import MapIcon from '@mui/icons-material/RoomOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import MapboxMap from "../common/MapboxMap";
import BatteryIcon from "../common/BatteryIcon";
import LastXDays from "../sensors/LastXDays";
import SensorsPanel from "../sensors/SensorsPanel";
import { locationsData } from "../../utils/constants";
import { queryData } from "../../utils/api";

function StatusIcon({ status, lastSeen, timezone = 'UTC' }) {
  if (!lastSeen || lastSeen === '1970-01-01T00:00:00Z') {
    return (
      <Tooltip
        title="No activity"
        componentsProps={{ tooltip: { sx: { background: '#000' } } }}
        placement="top"
      >
        <ErrorOutlineRoundedIcon style={{ color: '#9ca3af' }} />
      </Tooltip>
    );
  }
  
  // Ensure timestamp is treated as UTC by appending Z if not present
  const timestampUTC = lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z';
  const lastSeenDate = new Date(timestampUTC);
  const currentTime = new Date().getTime();
  
  // Format date - only apply timezone conversion for non-UTC timezones
  let formattedLastSeenDate;
  if (timezone === 'UTC') {
    formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC'
    });
  } else {
    formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
      timeZone: timezone,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  
  const expectedInterval = 3600; // 3600 seconds
  const expectedLastSeenTime = currentTime - expectedInterval * 1000;
  
  let icon;
  if (lastSeenDate.getTime() >= expectedLastSeenTime) {
    icon = <CheckCircleRoundedIcon className="text-green-600" />;
  } else {
    icon = <ErrorOutlineRoundedIcon style={{ color: '#b90501' }} />;
  }
  
  return (
    <Tooltip
      title={
        <>
          Last Seen: {formattedLastSeenDate}<br/>
          Expected Interval: {expectedInterval} sec
        </>
      }
      componentsProps={{ tooltip: { sx: { background: '#000' } } }}
      placement="top"
    >
      {icon}
    </Tooltip>
  );
}

export default function LocationDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTemp, setCurrentTemp] = useState(null);
  const [deviceStatuses, setDeviceStatuses] = useState({});
  const [sharedSensorData, setSharedSensorData] = useState(null);
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');

  // Update document title
  useEffect(() => {
    if (location) {
      document.title = `${location.name} - CROCUS Portal`;
    }
    return () => {
      document.title = 'CROCUS Portal';
    };
  }, [location]);
  
  // Listen for timezone changes
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  useEffect(() => {
    setLoading(true);
    
    // Find location from static data
    const loc = locationsData.find((l) => l.name === name);
    
    if (loc) {
      setLocation(loc);
      
      // Fetch current temperature - always use UTC for API queries
      queryData({
        start: "-1h",
        tail: 1,
        timezone: "UTC",
        filter: {
          location: name,
          name: "air_temperature"
        }
      }).then(data => {
        if (data.length > 0) {
          setCurrentTemp(data[0].value);
        }
      }).catch(err => {
        console.error("Failed to fetch temperature:", err);
      });
      
      // Single optimized query for all devices and their sensor data
      queryData({
        start: "-365d",
        tail: 1,
        timezone: "UTC",
        filter: {
          location: name
        }
      }).then(allData => {
        // Group data by device
        const dataByDevice = {};
        allData.forEach(record => {
          const deviceId = record.meta.DeviceID;
          if (!dataByDevice[deviceId]) {
            dataByDevice[deviceId] = [];
          }
          dataByDevice[deviceId].push(record);
        });
        
        // Process device statuses
        const statusMap = {};
        loc.devices.forEach(device => {
          const deviceData = dataByDevice[device.serialNumber] || [];
          
          let batteryVoltage = 3.7;
          let lastSeen = null;
          let status = "offline";
          
          if (deviceData.length > 0) {
            // Find battery voltage and most recent timestamp
            deviceData.forEach(record => {
              if (record.name === "battery_voltage") {
                batteryVoltage = record.value;
              }
              
              const timestampUTC = record.timestamp.endsWith('Z') 
                ? record.timestamp 
                : record.timestamp + 'Z';
              const recordTime = new Date(timestampUTC).getTime();
              
              if (!lastSeen || recordTime > new Date(lastSeen).getTime()) {
                lastSeen = record.timestamp;
              }
            });
            
            // Check if device is online based on expected interval (3600 seconds)
            if (lastSeen) {
              const lastSeenDate = new Date(lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z');
              const currentTime = new Date().getTime();
              const expectedLastSeenTime = currentTime - 3600 * 1000;
              
              if (lastSeenDate.getTime() >= expectedLastSeenTime) {
                status = "online";
              } else {
                status = "offline";
              }
            }
          }
          
          statusMap[device.serialNumber] = {
            status,
            batteryVoltage,
            lastSeen
          };
        });
        
        setDeviceStatuses(statusMap);
        
        // Prepare sensor data for SensorsPanel
        const sensorDataForPanel = {};
        Object.entries(dataByDevice).forEach(([deviceId, records]) => {
          const sensorData = {};
          records.forEach(record => {
            if (!sensorData[record.name] || 
                new Date(record.timestamp) > new Date(sensorData[record.name].timestamp)) {
              sensorData[record.name] = {
                value: record.value,
                timestamp: record.timestamp,
                unit: record.meta?.Unit,
              };
            }
          });
          sensorDataForPanel[deviceId] = sensorData;
        });
        
        setSharedSensorData(sensorDataForPanel);
        setLoading(false);
      }).catch(err => {
        console.error(`Failed to fetch data for location ${name}:`, err);
        setLoading(false);
      });
    } else {
      setError("Location not found");
      setLoading(false);
    }
  }, [name, timezone]);
  
  const handleDeviceClick = (device) => {
    navigate(`/device/${device.serialNumber}`, { 
      state: { device, location } 
    });
  };
  
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center p-12">
          <CircularProgress />
          <span className="ml-3">Loading location...</span>
        </div>
      </div>
    );
  }
  
  if (error || !location) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Alert severity="error">{error || "Location not found"}</Alert>
        <Link className="text-blue-600 underline mt-4 inline-block" to="/locations">Back to Locations</Link>
      </div>
    );
  }
  
  // Use device coordinates for map
  const sensorPositions = location.devices.map((device) => ({
    lat: device.coordinates[0],
    lon: device.coordinates[1],
    label: device.serialNumber,
    deviceName: device.name,
    status: deviceStatuses[device.serialNumber]?.status || "offline",
    lastSeen: deviceStatuses[device.serialNumber]?.lastSeen || null,
    deviceType: device.type,
    site: device.site,
    treeId: device.treeId,
    treeSpecies: device.treeSpecies,
    treeDiameter: device.treeDiameter,
    attachedSensors: device.attachedSensors,
    locationName: location.fullName,
    locationAbbrev: location.name,
    city: location.city
  }));
  
  const sitePhotos = location.sitePhotos || [];
  
  // Calculate location reporting status based on device data
  const isLocationReporting = () => {
    const now = Date.now();
    const thirtyMinutesAgo = now - (30 * 60 * 1000); // 30 minutes in milliseconds
    
    // Check if any device has reported in the last 30 minutes
    return location.devices.some(device => {
      const deviceStatus = deviceStatuses[device.serialNumber];
      if (!deviceStatus?.lastSeen) return false;
      
      const timestampUTC = deviceStatus.lastSeen.endsWith('Z') 
        ? deviceStatus.lastSeen 
        : deviceStatus.lastSeen + 'Z';
      const lastSeenTime = new Date(timestampUTC).getTime();
      
      return lastSeenTime >= thirtyMinutesAgo;
    });
  };
  
  const locationReporting = isLocationReporting();
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex items-baseline gap-2">
        <h1 className="text-2xl font-semibold">{location.fullName} ({location.name})</h1>
        <span 
          className="ml-auto text-sm"
          style={{ color: locationReporting ? '#16a34a' : '#b90501' }}
        >
          {locationReporting ? 'reporting' : 'not reporting'}
        </span>
      </div>
      
      <div className="mt-4 border rounded-md overflow-hidden bg-white">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-3 p-3">
          <div className="border rounded-md flex flex-col">
            <div className="bg-purple-900 text-white px-3 py-1.5 rounded-t">Overview</div>
            <div className="p-3 text-sm flex-1 flex flex-col">
              <dl className="grid grid-cols-3 gap-y-1.5">
                <dt className="text-gray-500">Project</dt>
                <dd className="col-span-2">CROCUS</dd>
                
                <dt className="text-gray-500">Location</dt>
                <dd className="col-span-2">{location.city}</dd>
                
                <dt className="text-gray-500">Site Type</dt>
                <dd className="col-span-2">{location.siteType}</dd>
                
                <dt className="text-gray-500">Registered</dt>
                <dd className="col-span-2">{location.overview.registration}</dd>
                
                <dt className="text-gray-500">GPS</dt>
                <dd className="col-span-2">
                  <div className="flex items-center gap-2">
                    <MapIcon className="text-sky-500" fontSize="small" />
                    {location.coordinates[0].toFixed(6)}, {location.coordinates[1].toFixed(6)}
                  </div>
                </dd>
                
                {currentTemp !== null && (
                  <>
                    <dt className="text-gray-500">Current Temperature</dt>
                    <dd className="col-span-2">
                      <div className="flex items-center gap-1">
                        <ThermostatIcon className="text-orange-500" fontSize="small" />
                        <span>{currentTemp}°C</span>
                      </div>
                    </dd>
                  </>
                )}
              </dl>
              
              {sitePhotos.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">Site Photos</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {sitePhotos.map((photo, idx) => (
                      <div key={idx} className="group cursor-pointer">
                        <div className="aspect-square overflow-hidden rounded border border-gray-200 hover:border-blue-400 transition-colors">
                          <img 
                            src={photo.url} 
                            alt={photo.caption}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">{photo.caption}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="border rounded-md flex flex-col">
            <div className="bg-purple-900 text-white px-3 py-1.5 rounded-t">Sensor Locations</div>
            <div className="p-3 flex-1 flex flex-col">
              <MapboxMap 
                data={sensorPositions} 
                height={370} 
                showLegend={true} 
                defaultZoom={17}
                showDeviceDetails={true}
              />
            </div>
          </div>
        </div>
        
        <div className="px-3 pb-4">
          <div className="border rounded-md overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
              <h2 className="font-semibold">Devices</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-purple-900 text-white">
                  <tr>
                    <th className="px-3 py-2 text-left w-[70px]">Status</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">DevEUI</th>
                    <th className="px-3 py-2 text-left">Site</th>
                    <th className="px-3 py-2 text-left">Tree Species</th>
                    <th className="px-3 py-2 text-left">Tree Diameter</th>
                    <th className="px-3 py-2 text-left">Battery Level</th>
                  </tr>
                </thead>
                <tbody>
                  {location.devices.map((device, i) => {
                    const deviceStatus = deviceStatuses[device.serialNumber] || { 
                      status: "offline", 
                      batteryVoltage: 3.7, 
                      lastSeen: null 
                    };
                    
                    return (
                      <tr 
                        key={i} 
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleDeviceClick(device)}
                      >
                        <td className="px-3 py-2">
                          <StatusIcon 
                            status={deviceStatus.status} 
                            lastSeen={deviceStatus.lastSeen}
                            timezone={timezone}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <a 
                            href={`/device/${device.serialNumber}`}
                            onClick={(e) => {
                              // Only prevent default for left clicks
                              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeviceClick(device);
                              }
                            }}
                            onMouseDown={(e) => {
                              // Handle middle click
                              if (e.button === 1) {
                                e.stopPropagation();
                              }
                            }}
                            className="text-blue-600 hover:underline"
                          >
                            {device.name}
                          </a>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{device.devEUI || "-"}</td>
                        <td className="px-3 py-2">{device.site || "-"}</td>
                        <td className="px-3 py-2">{device.treeSpecies || "-"}</td>
                        <td className="px-3 py-2">{device.treeDiameter || "-"}</td>
                        <td className="px-3 py-2">
                          {device.type === "WXT536 Sensor" || device.type === "AQT530 Sensor" 
                            ? "-" 
                            : <BatteryIcon batteryVoltage={deviceStatus.batteryVoltage} />
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          <LastXDays location={location} />
          <SensorsPanel location={location} sharedSensorData={sharedSensorData} loading={!sharedSensorData} />
        </div>
      </div>
    </div>
  );
}